'use client'

import { useState, FormEvent } from 'react'
import { LogIn, UserPlus } from 'lucide-react'
import { Language } from '@/lib/brief613/translations'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { panel, headingClass, mutedClass, errorBox, labelClass, fieldClass, primaryBtnInline } from './ui'

interface LoginFormProps {
  onSuccess: (user: { id: string; username: string; email: string; role: string; companyName?: string | null }) => void
  onSwitchToRegister: () => void
  language: Language
}

export default function LoginForm({ onSuccess, onSwitchToRegister, language }: LoginFormProps) {
  const t = useBrief613Translation(language)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const formData = new FormData(e.currentTarget)
    const username = formData.get('username')?.toString()
    const password = formData.get('password')?.toString()

    if (!username || !password) {
      setError(t.login.required)
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/brief613/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })

      const result = await response.json()

      if (result.success) {
        // Debug: log the user data
        console.log('[LoginForm] User logged in:', result.user)
        // Ensure role is set correctly
        if (result.user.username === 'admin' && result.user.role !== 'admin') {
          console.warn('[LoginForm] Admin user has wrong role, fixing...')
          result.user.role = 'admin'
        }
        onSuccess(result.user)
      } else {
        setError(result.error || t.login.invalidCredentials)
      }
    } catch (err: any) {
      setError(err.message || t.login.error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${panel} p-8 max-w-md mx-auto`}>
      <div className="text-center mb-6">
        <LogIn className="mx-auto mb-4 text-white/50" size={32} />
        <h2 className={headingClass}>{t.login.title}</h2>
        <p className={`${mutedClass} mt-2`}>{t.login.subtitle}</p>
      </div>

      {error && (
        <div className={`mb-6 ${errorBox}`}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className={labelClass}>{t.login.username}</label>
          <input
            type="text"
            name="username"
            required
            className={fieldClass}
            placeholder={t.login.usernamePlaceholder}
            disabled={loading}
          />
        </div>

        <div>
          <label className={labelClass}>{t.login.password}</label>
          <input
            type="password"
            name="password"
            required
            className={fieldClass}
            placeholder={t.login.passwordPlaceholder}
            disabled={loading}
          />
        </div>

        <div className="flex justify-between items-center gap-3">
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="text-white/55 hover:text-white/85 text-xs flex items-center gap-2 transition-colors"
            disabled={loading}
          >
            <UserPlus size={16} />
            {t.login.noAccount} {t.login.register}
          </button>
          <button
            type="submit"
            className={primaryBtnInline}
            disabled={loading}
          >
            {loading ? t.login.loggingIn : (
              <>
                <LogIn size={16} />
                {t.login.loginButton}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
